export const COREP_KCP_RETURN_CONTEXT_STORAGE_KEY =
  "cnfood-corep-kcp-return-context";
export const COREP_KCP_RETURN_ROUTE_TYPE = "COREP_INVITE";
export const COREP_KCP_RETURN_SUBJECT_TYPE = "CO_REP";

const COREP_KCP_RETURN_CONTEXT_TTL_MS = 30 * 60 * 1000;

const getSessionStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage || null;
  } catch {
    return null;
  }
};

const safeString = (value: unknown) => String(value || "").trim();

const sha256Hex = async (value = "") => {
  const source = safeString(value);
  if (!source) return "";
  if (
    typeof globalThis.crypto !== "undefined" &&
    globalThis.crypto.subtle &&
    typeof TextEncoder !== "undefined"
  ) {
    const bytes = new TextEncoder().encode(source);
    const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest))
      .map((item) => item.toString(16).padStart(2, "0"))
      .join("");
  }
  throw new Error("SHA256_UNAVAILABLE");
};

const hasSensitiveContextKey = (serialized = "") =>
  /"(ssnFront|ssnBack|residentNumber|ciEncrypted|diEncrypted|reg_cert_key|enc_data)"\s*:/i.test(
    serialized
  );

export const buildCorepKcpReturnContext = async ({
  corepInviteToken = "",
  corepInviteTokenHash = "",
  coRepresentativeId = "",
  kcpSessionId = "",
  orderId = "",
  createdAt = new Date().toISOString(),
}: {
  corepInviteToken?: string;
  corepInviteTokenHash?: string;
  coRepresentativeId?: string;
  kcpSessionId?: string;
  orderId?: string;
  createdAt?: string;
} = {}) => {
  const token = safeString(corepInviteToken);
  const tokenHash =
    safeString(corepInviteTokenHash) || (token ? await sha256Hex(token) : "");
  return {
    routeType: COREP_KCP_RETURN_ROUTE_TYPE,
    subjectType: COREP_KCP_RETURN_SUBJECT_TYPE,
    corepInviteToken: token,
    corepInviteTokenHash: tokenHash,
    coRepresentativeId: safeString(coRepresentativeId),
    kcpSessionId: safeString(kcpSessionId),
    orderId: safeString(orderId),
    createdAt: safeString(createdAt),
  };
};

export const isCorepKcpReturnContext = (value: any) =>
  Boolean(
    value &&
      value.routeType === COREP_KCP_RETURN_ROUTE_TYPE &&
      value.subjectType === COREP_KCP_RETURN_SUBJECT_TYPE &&
      value.kcpSessionId
  );

export const saveCorepKcpReturnContext = (context: any) => {
  const storage = getSessionStorage();
  if (!storage || !isCorepKcpReturnContext(context)) return false;
  const serialized = JSON.stringify(context);
  if (hasSensitiveContextKey(serialized)) {
    throw new Error("COREP_KCP_RETURN_CONTEXT_CONTAINS_SENSITIVE_KEY");
  }
  storage.setItem(COREP_KCP_RETURN_CONTEXT_STORAGE_KEY, serialized);
  return true;
};

export const loadCorepKcpReturnContext = () => {
  const storage = getSessionStorage();
  if (!storage) return null;
  const raw = storage.getItem(COREP_KCP_RETURN_CONTEXT_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!isCorepKcpReturnContext(parsed)) return null;
    const createdTime = Date.parse(parsed.createdAt || "");
    if (
      Number.isFinite(createdTime) &&
      Date.now() - createdTime > COREP_KCP_RETURN_CONTEXT_TTL_MS
    ) {
      storage.removeItem(COREP_KCP_RETURN_CONTEXT_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(COREP_KCP_RETURN_CONTEXT_STORAGE_KEY);
    return null;
  }
};

export const clearCorepKcpReturnContext = () => {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.removeItem(COREP_KCP_RETURN_CONTEXT_STORAGE_KEY);
};

export const shouldRouteToCorepInviteFlow = ({
  search = "",
  context = null,
}: {
  search?: string;
  context?: any;
} = {}) => {
  const source =
    search ||
    (typeof window !== "undefined" ? window.location.search || "" : "");
  try {
    const params = new URLSearchParams(source);
    if (params.has("corepInvite")) return true;
    if (params.get("subjectType") === COREP_KCP_RETURN_SUBJECT_TYPE) return true;
  } catch {
    // Fall back to persisted context.
  }
  return isCorepKcpReturnContext(context || loadCorepKcpReturnContext());
};

export const getCorepInviteTokenFromCorepRoute = ({
  search = "",
  context = null,
}: {
  search?: string;
  context?: any;
} = {}) => {
  const source =
    search ||
    (typeof window !== "undefined" ? window.location.search || "" : "");
  try {
    const token = new URLSearchParams(source).get("corepInvite");
    if (token) return token;
  } catch {
    // Fall back to persisted context.
  }
  const effectiveContext = context || loadCorepKcpReturnContext();
  return safeString(effectiveContext?.corepInviteToken);
};

export const getCorepInviteTokenHashFromCorepRoute = ({
  search = "",
  context = null,
}: {
  search?: string;
  context?: any;
} = {}) => {
  const source =
    search ||
    (typeof window !== "undefined" ? window.location.search || "" : "");
  try {
    const tokenHash = new URLSearchParams(source).get("corepInviteTokenHash");
    if (tokenHash) return safeString(tokenHash);
  } catch {
    // Fall back to persisted context.
  }
  const effectiveContext = context || loadCorepKcpReturnContext();
  return safeString(effectiveContext?.corepInviteTokenHash);
};

const getCorepIdentityRawStatus = (status: any) =>
  safeString(
    status?.status ||
      status?.verificationStatus ||
      status?.representativeIdentityVerification?.status
  ).toUpperCase();

const hasCorepIdentityEvidence = (status: any) => {
  const identity = status?.representativeIdentityVerification || {};
  const hasCi = Boolean(
    status?.hasCi === true ||
      status?.ciHash ||
      status?.ciHmac ||
      identity?.hasCi === true ||
      identity?.ciHash ||
      identity?.ciHmac
  );
  const hasDi = Boolean(
    status?.hasDi === true ||
      status?.diHash ||
      status?.diHmac ||
      identity?.hasDi === true ||
      identity?.diHash ||
      identity?.diHmac
  );
  return { hasCi, hasDi };
};

export const isCorepIdentityComplete = ({
  status = null,
  coRepresentativeId = "",
}: {
  status?: any;
  coRepresentativeId?: string;
} = {}) => {
  if (!status) return false;
  if (status.subjectType !== COREP_KCP_RETURN_SUBJECT_TYPE) return false;
  if (safeString(status.coRepresentativeId) !== safeString(coRepresentativeId)) {
    return false;
  }
  const rawStatus = getCorepIdentityRawStatus(status);
  const identity = status.representativeIdentityVerification || {};
  const checks = identity.checks || status.checks || {};
  const matched =
    rawStatus === "MATCHED" ||
    status.representativeMatchStatus === "MATCHED" ||
    identity.status === "MATCHED" ||
    (checks.nameMatched === true &&
      checks.birthDateMatched === true &&
      checks.phoneMatched === true);
  const mismatched =
    rawStatus === "MISMATCHED" ||
    status.representativeMatchStatus === "MISMATCHED" ||
    identity.status === "MISMATCHED";
  const { hasCi, hasDi } = hasCorepIdentityEvidence(status);
  return (
    (rawStatus === "VERIFIED" || rawStatus === "MATCHED") &&
    matched &&
    !mismatched &&
    hasCi &&
    hasDi
  );
};

export const normalizeCorepIdentityStatus = ({
  status = null,
  coRepresentativeId = "",
}: {
  status?: any;
  coRepresentativeId?: string;
} = {}) => {
  const rawStatus = getCorepIdentityRawStatus(status);
  const base = {
    rawStatus: rawStatus || "NOT_STARTED",
    displayLabel: "본인확인 전",
    description: "공동계약인 본인 명의 휴대폰으로 본인확인을 진행해 주세요.",
    canProceed: false,
    needsRefresh: false,
    tone: "gray",
  };
  if (!status) return base;

  if (status.subjectType && status.subjectType !== COREP_KCP_RETURN_SUBJECT_TYPE) {
    return {
      ...base,
      rawStatus: rawStatus || "INVALID_SUBJECT",
      displayLabel: "본인확인 불일치",
      description:
        "공동계약인 본인확인 결과가 아닙니다. 공동계약인 전용 링크에서 다시 진행해 주세요.",
      tone: "red",
    };
  }

  if (
    safeString(coRepresentativeId) &&
    safeString(status.coRepresentativeId) !== safeString(coRepresentativeId)
  ) {
    return {
      ...base,
      rawStatus: rawStatus || "ID_MISMATCH",
      displayLabel: "본인확인 불일치",
      description:
        "본인확인 결과가 현재 공동계약인 정보와 일치하지 않습니다. 공동계약인 본인 명의 휴대폰으로 다시 인증해 주세요.",
      tone: "red",
    };
  }

  if (isCorepIdentityComplete({ status, coRepresentativeId })) {
    return {
      ...base,
      rawStatus: rawStatus || "VERIFIED",
      displayLabel: "본인확인 완료",
      description: "공동계약인 본인확인이 완료되었습니다. 계약 내용을 확인해 주세요.",
      canProceed: true,
      needsRefresh: false,
      tone: "green",
    };
  }

  const identity = status.representativeIdentityVerification || {};
  const mismatch =
    rawStatus === "MISMATCHED" ||
    status.representativeMatchStatus === "MISMATCHED" ||
    identity.status === "MISMATCHED";
  if (mismatch) {
    return {
      ...base,
      rawStatus: rawStatus || "MISMATCHED",
      displayLabel: "본인확인 불일치",
      description:
        "공동계약인 본인확인 정보가 입력한 정보와 일치하지 않습니다. 공동계약인 본인 명의 휴대폰으로 다시 인증해 주세요.",
      tone: "red",
    };
  }

  if (rawStatus === "FAILED") {
    return {
      ...base,
      rawStatus,
      displayLabel: "본인확인 실패",
      description:
        "공동계약인 본인확인이 완료되지 않았습니다. 정보를 확인한 뒤 다시 진행해 주세요.",
      tone: "red",
    };
  }

  if (rawStatus === "EXPIRED") {
    return {
      ...base,
      rawStatus,
      displayLabel: "본인확인 시간 만료",
      description: "본인확인 시간이 만료되었습니다. 다시 본인확인을 진행해 주세요.",
      tone: "amber",
    };
  }

  if (rawStatus === "REGISTERED") {
    return {
      ...base,
      rawStatus,
      displayLabel: "본인확인 요청 등록됨",
      description:
        "본인확인이 아직 완료로 확인되지 않았습니다. 인증을 완료했다면 잠시 후 자동으로 확인되거나 상태 새로고침을 눌러 주세요.",
      needsRefresh: true,
      tone: "blue",
    };
  }

  if (rawStatus === "PENDING" || rawStatus === "PENDING_KCP" || rawStatus === "VERIFIED") {
    return {
      ...base,
      rawStatus: rawStatus || "PENDING",
      displayLabel: "본인확인 진행 중",
      description:
        "본인확인 완료 상태를 확인하고 있습니다. 인증을 완료했다면 상태 새로고침을 눌러 주세요.",
      needsRefresh: true,
      tone: "amber",
    };
  }

  return {
    ...base,
    rawStatus: rawStatus || "NOT_STARTED",
    displayLabel: "본인확인 전",
    description: "공동계약인 본인 명의 휴대폰으로 본인확인을 진행해 주세요.",
    needsRefresh: Boolean(status?.sessionId),
    tone: "gray",
  };
};

export const validateCorepKcpStatusForInvite = ({
  status = null,
  coRepresentativeId = "",
}: {
  status?: any;
  coRepresentativeId?: string;
} = {}) => {
  if (!status) {
    return {
      ok: false,
      verified: false,
      code: "CO_REPRESENTATIVE_KCP_STATUS_MISSING",
    };
  }
  if (status.subjectType !== COREP_KCP_RETURN_SUBJECT_TYPE) {
    return {
      ok: false,
      verified: false,
      code: "CO_REPRESENTATIVE_KCP_SUBJECT_MISMATCH",
    };
  }
  if (
    safeString(status.coRepresentativeId) !== safeString(coRepresentativeId)
  ) {
    return {
      ok: false,
      verified: false,
      code: "CO_REPRESENTATIVE_KCP_ID_MISMATCH",
    };
  }
  const verified = isCorepIdentityComplete({ status, coRepresentativeId });
  return {
    ok: true,
    verified,
    code: verified ? "CO_REPRESENTATIVE_KCP_VERIFIED" : "CO_REPRESENTATIVE_KCP_PENDING",
  };
};
